import type { AppFile } from '@lib/types/app'

/**
 * Checks if the app provides the UI itself (is the slotty-ui app)
 */
export const isTheUiItself = (app: AppFile): boolean => !!app?.app_metadata?.provide?.includes('ui')

/**
 * Checks if the app is a postcall app
 */
export const isPostcallApp = (app: AppFile): boolean => !!app?.app_metadata?.provide?.includes('postcall')

/**
 * Checks if the app replaces the file-viewer module
 */
export const isFileViewerReplacement = (app: AppFile): boolean =>
  !!app?.app_metadata?.replaces?.includes('file-viewer') ||
  !!app?.app_metadata?.replaces?.some(
    (r: string | { module: string }) => r && typeof r !== 'string' && r.module === 'file-viewer',
  )

/**
 * Checks if the app has no module, hooks, or app_type defined (legacy app)
 */
export const doesNotHaveAnyModuleOrHookSpecified = (app: AppFile): boolean =>
  !app?.app_metadata?.module && !app?.app_metadata?.hooks && !app?.app_metadata?.app_type

/**
 * Checks if the app has an app_type defined
 */
export const hasAppTypeDefined = (app: AppFile): boolean => !!app?.app_metadata?.app_type

/**
 * Checks if the app is a canvas section execution app
 */
export const isCanvasSectionExecution = (app: AppFile): boolean =>
  !!app?.app_metadata?.module?.canvas_section_execution?.enabled

/**
 * Checks if the app is a canvas drawer app
 */
export const isCanvasDrawerApp = (app: AppFile): boolean => !!app?.app_metadata?.module?.canvas_drawer?.enabled

/**
 * Checks if the app requires CRM connection
 */
export const appRequiresCrm = (app: AppFile): boolean => !!app?.app_metadata?.require?.includes('crm')

/**
 * Determines if an app should be shown in the slotty-ui sidebar.
 * An app shows in sidebar if:
 * - It has `module.ui_app.enabled = true`, OR
 * - It's a legacy app (no module, hooks, or app_type defined) AND
 *   it's not a postcall app, file-viewer replacement, canvas drawer,
 *   canvas section execution, or has app_type defined
 */
export const shouldShowInSidebar = (app: AppFile): boolean =>
  !isTheUiItself(app) &&
  (!!app?.app_metadata?.module?.ui_app?.enabled ||
    (doesNotHaveAnyModuleOrHookSpecified(app) &&
      !isPostcallApp(app) &&
      !isFileViewerReplacement(app) &&
      !isCanvasDrawerApp(app) &&
      !isCanvasSectionExecution(app) &&
      !hasAppTypeDefined(app)))

/**
 * Filters an array of AppFiles to only include those that should show in sidebar
 */
export const filterSidebarApps = (apps: AppFile[]): AppFile[] => apps.filter(shouldShowInSidebar)
